import React, { FC, forwardRef, memo, useMemo } from "react";
import { Avatar, AvatarBadge, Box, Circle } from "@chakra-ui/react";
import { useUsers } from "modules/providers/users-provider";
import colors from "app/colors";
import {
  AvatarIcon,
  DoNotDisturbIcon,
  PhoneSmsAvatarIcon,
  UserEmailCircleIcon,
  SyncoUserAvatarIcon,
} from "modules/icons";
import {
  getUserFullName,
  isUserOutOfOffice,
  isUserSmsOnlyAndNotLoggedIn,
} from "modules/utils/Functions";
import { UserFragment, UserRoleType } from "gql/graphql";
import { useUserPresence } from "modules/providers/user-presence-provider";

interface IProps {
  userId: string;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "2xs" | "xs" | "full";
  displayOnlineIndicator?: boolean;
  badgeBorderColor?: string;
  /** Exact width/height (e.g. `"32px"`). Overrides theme sizing from `size`. */
  boxSizePx?: string;
  /** Border radius (e.g. `"16px"` for a circle when `boxSizePx` is 32px). */
  borderRadiusPx?: string;
}

const TEAMSYNCO_EMAIL_DOMAIN = "@teamsynco.com";

const UserAvatar = forwardRef<any, IProps>(
  (
    {
      userId,
      size = "md",
      displayOnlineIndicator = true,
      badgeBorderColor = colors.white,
      boxSizePx,
      borderRadiusPx,
    },
    ref,
  ) => {
    const { usersById } = useUsers();
    const user = usersById[userId];

    const cloudfrontAvatarUrl = useMemo(() => {
      if (!user) return undefined;
      if (user.active && user.cloudfrontAvatarUrl) {
        return user.cloudfrontAvatarUrl;
      }
    }, [user]);

    const isTeamsyncoEmail = useMemo(() => {
      if (!user || user.roleType !== "EMAIL" || !user.email) return false;
      return user.email.toLowerCase().endsWith(TEAMSYNCO_EMAIL_DOMAIN);
    }, [user]);

    const resolvedBoxSize = useMemo(() => {
      if (boxSizePx) return boxSizePx;
      switch (size) {
        case "2xs":
          return "16px";
        case "xs":
          return "24px";
        case "sm":
          return "32px";
        case "md":
          return "48px";
        case "lg":
          return "64px";
        case "xl":
          return "96px";
        case "2xl":
          return "128px";
        default:
          return 6;
      }
    }, [boxSizePx, size]);

    const avatarFrameProps = useMemo(
      () =>
        borderRadiusPx
          ? { borderRadius: borderRadiusPx, overflow: "hidden" as const }
          : {},
      [borderRadiusPx],
    );

    const badgeSize = useMemo(() => {
      switch (size) {
        case "xs":
          return 2;
        case "sm":
          return 3;
        case "xl":
          return 8;
        default:
          return 4;
      }
    }, [size]);

    if (!user) return null;

    if (isTeamsyncoEmail) {
      const icon = (
        <SyncoUserAvatarIcon
          boxSize={borderRadiusPx ? "full" : resolvedBoxSize}
        />
      );
      return borderRadiusPx ? (
        <Box
          flexShrink={0}
          boxSize={resolvedBoxSize}
          {...avatarFrameProps}
        >
          {icon}
        </Box>
      ) : (
        icon
      );
    }

    if (user.roleType === UserRoleType.Email) {
      const icon = (
        <UserEmailCircleIcon
          boxSize={borderRadiusPx ? "full" : resolvedBoxSize}
          fill={colors.white}
        />
      );
      return borderRadiusPx ? (
        <Box
          flexShrink={0}
          boxSize={resolvedBoxSize}
          {...avatarFrameProps}
        >
          {icon}
        </Box>
      ) : (
        icon
      );
    }

    if (user.roleType === UserRoleType.Api) {
      return (
        <Avatar
          ref={ref}
          src={cloudfrontAvatarUrl}
          size={size}
          boxSize={resolvedBoxSize}
          {...(borderRadiusPx ? { borderRadius: borderRadiusPx } : {})}
        />
      );
    }

    if (isUserSmsOnlyAndNotLoggedIn(user)) {
      return (
        <Avatar
          ref={ref}
          name={user ? getUserFullName(user) : ""}
          size={size}
          boxSize={resolvedBoxSize}
          {...(borderRadiusPx ? { borderRadius: borderRadiusPx } : {})}
          bg={colors.purple30}
          color={colors.purple10}
          fontWeight={700}
        >
          {displayOnlineIndicator && (
            <AvatarBadge bg="unset" border="unset">
              <PhoneSmsAvatarIcon
                boxSize={badgeSize + 1}
                fill={colors.darkBlue}
              />
            </AvatarBadge>
          )}
        </Avatar>
      );
    }

    //user has never logged onto the platform
    if (!user.firstLoginTime) {
      return (
        <Avatar
          ref={ref}
          name={user ? getUserFullName(user) : ""}
          size={size}
          boxSize={resolvedBoxSize}
          {...(borderRadiusPx ? { borderRadius: borderRadiusPx } : {})}
          bg={colors.gray0}
          color={colors.gray30}
          fontWeight={700}
        >
          {displayOnlineIndicator && (
            <AvatarBadge boxSize={badgeSize} bg={colors.white}>
              <Circle
                size={badgeSize - 1}
                borderColor={colors.grayMain}
                borderWidth={1}
                bg="unset"
              />
            </AvatarBadge>
          )}
        </Avatar>
      );
    }

    if (user.active) {
      return (
        <Avatar
          ref={ref}
          name={user ? getUserFullName(user) : ""}
          size={size}
          boxSize={resolvedBoxSize}
          {...(borderRadiusPx ? { borderRadius: borderRadiusPx } : {})}
          src={cloudfrontAvatarUrl}
          fontWeight={700}
          ignoreFallback
        >
          <Badge
            user={user}
            displayOnlineIndicator={displayOnlineIndicator}
            badgeSize={badgeSize}
            bg={colors.greenPrimary}
            borderColor={badgeBorderColor}
          />
        </Avatar>
      );
    }

    //Inactive user
    return (
      <Avatar
        ref={ref}
        size={size}
        boxSize={resolvedBoxSize}
        {...(borderRadiusPx ? { borderRadius: borderRadiusPx } : {})}
        fontWeight={700}
        icon={<AvatarIcon fill={colors.white} boxSize="full" />}
        backgroundColor="unset"
      />
    );
  },
);

export default memo(UserAvatar);

interface BadgeProps {
  user: UserFragment;
  displayOnlineIndicator: boolean;
  badgeSize?: number;
  bg?: string;
  borderColor?: string;
}

const Badge: FC<BadgeProps> = ({
  user,
  displayOnlineIndicator,
  badgeSize = 4,
  bg = colors.greenPrimary,
  borderColor = colors.white,
}) => {
  const { isUserOnline } = useUserPresence();

  if (!displayOnlineIndicator) {
    return null;
  }

  if (isUserOutOfOffice(user)) {
    return (
      <AvatarBadge borderColor={borderColor}>
        <DoNotDisturbIcon boxSize="0.5em" fill={colors.red} />
      </AvatarBadge>
    );
  }

  if (isUserOnline(user.id)) {
    return (
      <AvatarBadge boxSize={badgeSize} bg={bg} borderColor={borderColor} />
    );
  }

  return null;
};
